/* ============================================================
   STENS — Main JS
   Theme toggle · Vehicle pill · Modal · Mobile menu · Lang
   ============================================================ */
(function() {
  'use strict';

  // ============================================================
  // 1. THEME TOGGLE (light/dark with localStorage)
  // ============================================================
  function initTheme() {
    const STORAGE_KEY = 'stens_theme';
    const root = document.documentElement;
    const themeToggle = document.getElementById('themeToggle');

    // Default to light if nothing stored
    let saved = null;
    try { saved = localStorage.getItem(STORAGE_KEY); } catch (e) {}

    if (saved === 'dark' || saved === 'light') {
      root.setAttribute('data-theme', saved);
    } else {
      // default light, no attribute (light is the default)
      root.removeAttribute('data-theme');
    }

    if (themeToggle) {
      themeToggle.addEventListener('click', () => {
        const current = root.getAttribute('data-theme');
        const next = current === 'dark' ? 'light' : 'dark';
        if (next === 'dark') {
          root.setAttribute('data-theme', 'dark');
        } else {
          root.removeAttribute('data-theme');
        }
        try { localStorage.setItem(STORAGE_KEY, next); } catch (e) {}
      });
    }
  }

  // ============================================================
  // 2. VEHICLE PILL (header — opens finder modal, shows saved vehicle)
  // ============================================================
  function initVehicleHeader() {
    const pill = document.getElementById('vehiclePill');
    const labelEl = document.getElementById('vehiclePillLabel');
    const valueEl = document.getElementById('vehiclePillValue');
    const clearBtn = document.getElementById('vehiclePillClear');

    function updateFromStorage() {
      let vehicle = null;
      try {
        const raw = sessionStorage.getItem('stens_vehicle');
        if (raw) vehicle = JSON.parse(raw);
      } catch (e) {}

      if (vehicle && vehicle.make && vehicle.model) {
        if (pill) pill.classList.add('is-active');
        if (labelEl) labelEl.textContent = 'Selected vehicle';
        if (valueEl) valueEl.textContent = `${vehicle.make} ${vehicle.model}`;
      } else {
        if (pill) pill.classList.remove('is-active');
        if (labelEl) labelEl.textContent = 'Find your vehicle';
        if (valueEl) valueEl.textContent = 'Make · Model · Year · Engine';
      }
    }

    document.addEventListener('stens:vehicle-selected', updateFromStorage);
    document.addEventListener('stens:vehicle-cleared', updateFromStorage);

    if (clearBtn) {
      clearBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        try { sessionStorage.removeItem('stens_vehicle'); } catch (err) {}
        document.dispatchEvent(new CustomEvent('stens:vehicle-cleared'));
      });
    }

    updateFromStorage();
  }

  // ============================================================
  // 3. LANGUAGE SWITCH (placeholder — not switching languages, just demo)
  // ============================================================
  function initLangSwitch() {
    const btn = document.getElementById('langSwitch');
    if (!btn) return;
    btn.addEventListener('click', () => {
      // Demo only — could open a real menu in production
      btn.style.borderColor = btn.style.borderColor ? '' : 'var(--c-border)';
    });
  }

  // ============================================================
  // 4. MOBILE MENU TOGGLE
  // ============================================================
  function initMobileMenu() {
    const toggle = document.getElementById('menuToggle');
    const nav = document.getElementById('mainNav');
    if (!toggle || !nav) return;

    function closeMenu() {
      nav.classList.remove('is-mobile-open');
      toggle.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
    }

    toggle.addEventListener('click', () => {
      const isOpen = !nav.classList.contains('is-mobile-open');
      nav.classList.toggle('is-mobile-open', isOpen);
      toggle.classList.toggle('is-open', isOpen);
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });

    nav.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', closeMenu);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeMenu();
    });
  }

  // ============================================================
  // 5. FOOTER ACCORDION (mobile)
  // ============================================================
  function initFooterAccordion() {
    const triggers = document.querySelectorAll('.footer-accordion__trigger');
    if (!triggers.length) return;

    const mq = window.matchMedia('(max-width: 768px)');

    function resetAccordions() {
      triggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
      });
    }

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        if (!mq.matches) return;
        const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
        resetAccordions();
        trigger.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
      });
    });

    mq.addEventListener('change', (e) => {
      if (!e.matches) resetAccordions();
    });
  }

  // ============================================================
  // 6. FINDER MODAL (open/close, body lock)
  // ============================================================
  function initFinderModal() {
    const backdrop = document.getElementById('vfModalBackdrop');
    const closeBtn = document.getElementById('vfModalClose');
    if (!backdrop) return;

    let savedScrollY = 0;

    function openModal() {
      // Save scroll
      savedScrollY = window.scrollY || document.documentElement.scrollTop || 0;

      backdrop.classList.add('is-open');
      backdrop.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('is-modal-open');
      document.body.classList.add('is-modal-open');
      document.body.style.top = `-${savedScrollY}px`;

      // Reset display state
      const modalRow = backdrop.querySelector('.vfinder-row');
      const modalSubmit = backdrop.querySelector('#vfSubmit');
      const submitRow = backdrop.querySelector('.vf-modal-submit-row');
      const modalResult = backdrop.querySelector('#vfModalResult');
      if (modalRow) modalRow.style.display = '';
      if (submitRow) submitRow.style.display = '';
      if (modalResult) modalResult.style.display = 'none';

      // Refresh finder state
      if (typeof window.STENS_refreshFinder === 'function') {
        window.STENS_refreshFinder();
      }

      setTimeout(() => {
        const firstToggle = backdrop.querySelector('[data-vf-field="make"] .vf-dropdown-toggle');
        if (firstToggle && !firstToggle.disabled) firstToggle.focus();
      }, 350);
    }

    function closeModal() {
      backdrop.classList.remove('is-open');
      backdrop.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('is-modal-open');
      document.body.classList.remove('is-modal-open');
      document.body.style.top = '';
      window.scrollTo(0, savedScrollY);
    }

    // Open from any [data-action="open-finder"] trigger
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-action="open-finder"]');
      if (trigger) {
        e.preventDefault();
        // Don't open if click was on the clear button
        if (e.target.closest('.pill-clear')) return;
        openModal();
      }
    });

    // Close handlers
    if (closeBtn) closeBtn.addEventListener('click', closeModal);

    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) closeModal();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && backdrop.classList.contains('is-open')) {
        closeModal();
      }
    });

    // Expose for vfinder.js to close after success
    window.STENS_closeModal = closeModal;
  }

  // ============================================================
  // INIT ALL
  // ============================================================
  function init() {
    initTheme();
    initVehicleHeader();
    initLangSwitch();
    initMobileMenu();
    initFooterAccordion();
    initFinderModal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
